import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { 
  Shield, ExternalLink, CheckCircle, AlertCircle, Loader2, 
  HelpCircle, Eye, EyeOff, Link2, RefreshCw
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function ConnectorConfigDialog({ 
  open, 
  onOpenChange, 
  connectorType, 
  existingConnector = null,
  onSuccess 
}) {
  const [typeInfo, setTypeInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [formData, setFormData] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [editingSecrets, setEditingSecrets] = useState({});
  const [name, setName] = useState('');


  // Fetch connector type info
  useEffect(() => {
    if (open && connectorType) {
      fetchTypeInfo();
      if (existingConnector) {
        setName(existingConnector.name);
        setFormData(existingConnector.config || {});
      } else {
        setName('');
        setFormData({});
      }
      setTestResult(null);
      setShowSecrets({});
      setEditingSecrets({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, connectorType, existingConnector]);

  const fetchTypeInfo = async () => {
    try {
      const response = await api.get('/connectors/types');
      const types = response.data.connector_types || {};
      setTypeInfo(types[connectorType] || null);
      
      // Initialize form with defaults
      if (types[connectorType] && !existingConnector) {
        const defaults = {};
        types[connectorType].fields?.forEach(field => {
          if (field.default !== undefined) {
            defaults[field.key] = field.default;
          }
        });
        setFormData(defaults);
      }
    } catch (err) {
      console.error('Failed to fetch connector types:', err);
    }
  };

  const handleFieldChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setTestResult(null); // Clear test result on change
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Name required');
      return;
    }

    // Validate required fields
    for (const field of (typeInfo?.fields || [])) {
      if (field.required && !formData[field.key]) {
        toast.error(`${field.label} is required`);
        return;
      }
    }

    if (connectorType === 'sharepoint') {
      const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuid.test(formData.tenant_id || '') || !uuid.test(formData.client_id || '')) {
        toast.error('Enter valid Microsoft tenant and application IDs from your IT administrator.');
        return;
      }
    }

    setLoading(true);
    try {
      if (existingConnector) {
        await api.put(`/connectors/${existingConnector.id}`, {
          connector_type: connectorType,
          name,
          config: formData
        });
        toast.success('Connector updated');
      } else {
        await api.post('/connectors', {
          connector_type: connectorType,
          name,
          config: formData
        });
        toast.success('Connector created', { description: 'Test the connection to verify it works.' });
      }
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error('Error', { description: err.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (!existingConnector) {
      toast.error('Save first', { description: 'Save the connector before testing' });
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    try {
      const response = await api.post(`/connectors/${existingConnector.id}/test`);
      setTestResult(response.data);
      if (response.data.success) {
        toast.success('Connection successful!');
        onSuccess?.();
      } else {
        toast.error('Connection failed');
      }
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || err.message });
      toast.error('Test failed', { description: err.response?.data?.detail });
    } finally {
      setTesting(false);
    }
  };

  if (!typeInfo) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-primary" />
            {existingConnector ? 'Edit' : 'Configure'} {typeInfo.name} Connector
          </DialogTitle>
          <DialogDescription>
            {typeInfo.description || `Connect your ${typeInfo.name} to import documents into the knowledge base`}
          </DialogDescription>
        </DialogHeader>

        {/* Security Notice */}
        <div className="flex items-start gap-3 p-3 bg-success/10  border border-success/30  rounded-lg">
          <Shield className="w-5 h-5 text-success mt-0.5" />
          <div>
            <p className="text-sm font-medium text-success ">
              Your credentials are secure
            </p>
            <p className="text-xs text-success dark:text-success mt-1">
              Credentials are encrypted at rest using authenticated encryption.
              Data is isolated within your tenant&apos;s environment and never shared.
            </p>
          </div>
        </div>

        {/* Connector Name */}
        <div className="space-y-2">
          <Label htmlFor="connector-name">Connector Name *</Label>
          <Input
            id="connector-name"
            name="connector-display-name"
            autoComplete="off"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`My ${typeInfo.name} Connection`}
            data-testid="connector-name-input"
          />
          <p className="text-xs text-muted-foreground">
            A friendly name to identify this connection
          </p>
        </div>

        {/* Dynamic Fields */}
        <div className="space-y-4 mt-4">
          {typeInfo.fields?.map((field) => (
            <div key={field.key} className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor={field.key}>
                  {field.label} {field.required && <span className="text-destructive">*</span>}
                </Label>
                {field.help && (
                  <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" title={field.help} />
                )}
              </div>
              
              {field.type === 'checkbox' ? (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={field.key}
                    checked={formData[field.key] ?? field.default ?? false}
                    onCheckedChange={(checked) => handleFieldChange(field.key, checked)}
                  />
                  <Label htmlFor={field.key} className="text-sm font-normal">
                    {field.label}
                  </Label>
                </div>
              ) : field.type === 'password' ? (
                !editingSecrets[field.key] ? (
                  <div className="space-y-2">
                    {formData[field.key] && <p className="text-sm text-muted-foreground">Saved credential will be kept unless replaced.</p>}
                    <Button type="button" variant="outline" onClick={() => {
                      setEditingSecrets(prev => ({ ...prev, [field.key]: true }));
                    }}>
                      {formData[field.key] ? 'Replace' : 'Enter'} {field.label.toLowerCase()}
                    </Button>
                  </div>
                ) : <div className="relative">
                  <Input
                    id={field.key}
                    name={`connector-${connectorType}-${field.key}`}
                    autoComplete="new-password"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    type={showSecrets[field.key] ? 'text' : 'password'}
                    value={String(formData[field.key] || '').startsWith('••••') ? '' : formData[field.key] || ''}
                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                    placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    aria-label={`${showSecrets[field.key] ? 'Hide' : 'Show'} ${field.label.toLowerCase()}`}
                    onClick={() => setShowSecrets(prev => ({ ...prev, [field.key]: !prev[field.key] }))}
                  >
                    {showSecrets[field.key] ? (
                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <Eye className="w-4 h-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              ) : (
                <Input
                  id={field.key}
                  name={`connector-${connectorType}-${field.key}`}
                  autoComplete="off"
                  type="text"
                  value={formData[field.key] || ''}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                />
              )}
              
              {field.help && (
                <p className="text-xs text-muted-foreground">{field.help}</p>
              )}
            </div>
          ))}
        </div>

        {/* Required Permissions */}
        {typeInfo.permissions_required && typeInfo.permissions_required.length > 0 && (
          <div className="mt-4 p-3 bg-secondary dark:bg-card/50 rounded-lg">
            <p className="text-sm font-medium text-foreground mb-2">Required Permissions</p>
            <div className="flex flex-wrap gap-2">
              {typeInfo.permissions_required.map((perm, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {perm}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Setup Guide Link */}
        {typeInfo.setup_guide_url && (
          <div className="flex items-center gap-2 text-sm">
            <ExternalLink className="w-4 h-4 text-muted-foreground" />
            <a 
              href={typeInfo.setup_guide_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              View setup guide for {typeInfo.name}
            </a>
          </div>
        )}

        {/* Test Result */}
        {testResult && (
          <div className={`p-3 rounded-lg ${
            testResult.success 
              ? 'bg-success/10  border border-success/30 ' 
              : 'bg-destructive/10  border border-destructive/30 '
          }`}>
            <div className="flex items-center gap-2">
              {testResult.success ? (
                <CheckCircle className="w-5 h-5 text-success" />
              ) : (
                <AlertCircle className="w-5 h-5 text-destructive" />
              )}
              <span className={`font-medium ${testResult.success ? 'text-success ' : 'text-destructive '}`}>
                {testResult.message}
              </span>
            </div>
            {testResult.details && (
              <div className="mt-2 text-sm space-y-1">
                {Object.entries(testResult.details).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}:</span>
                    <span className={testResult.success ? 'text-success ' : 'text-destructive '}>
                      {typeof value === 'boolean' ? (value ? '✓' : '✗') : value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {existingConnector && (
            <Button 
              variant="outline" 
              onClick={handleTest}
              disabled={testing}
              className="mr-auto"
            >
              {testing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Test saved connection
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {existingConnector ? 'Update' : 'Save'} Connector
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
